export const NO_SEARCH_MATCH = Number.POSITIVE_INFINITY;

const MIN_FUZZY_TOKEN_LENGTH = 4;

export function normalizeSearchText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function compactSearchText(value: unknown): string {
  return normalizeSearchText(value).replace(/\s+/g, '');
}

export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function maxAllowedEdits(tokenLength: number): number {
  if (tokenLength < MIN_FUZZY_TOKEN_LENGTH) return 0;
  return tokenLength >= 8 ? 2 : 1;
}

function boundedDamerauLevenshtein(source: string, target: string, maxDistance: number): number {
  if (source === target) return 0;
  if (Math.abs(source.length - target.length) > maxDistance) return maxDistance + 1;

  let previousPrevious: number[] | null = null;
  let previous = Array.from({ length: target.length + 1 }, (_, index) => index);

  for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex += 1) {
    const current = [sourceIndex];

    for (let targetIndex = 1; targetIndex <= target.length; targetIndex += 1) {
      const substitutionCost = source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1;
      let distance = Math.min(
        current[targetIndex - 1] + 1,
        previous[targetIndex] + 1,
        previous[targetIndex - 1] + substitutionCost,
      );

      if (
        previousPrevious &&
        sourceIndex > 1 &&
        targetIndex > 1 &&
        source[sourceIndex - 1] === target[targetIndex - 2] &&
        source[sourceIndex - 2] === target[targetIndex - 1]
      ) {
        distance = Math.min(distance, previousPrevious[targetIndex - 2] + 1);
      }

      current[targetIndex] = distance;
    }

    previousPrevious = previous;
    previous = current;
  }

  return previous[target.length];
}

function getTokenScore(queryToken: string, targetToken: string): number {
  if (queryToken === targetToken) return 0;
  if (targetToken.startsWith(queryToken)) return 0.1;
  if (targetToken.includes(queryToken)) return 0.2;

  const maxDistance = maxAllowedEdits(queryToken.length);
  if (maxDistance === 0) return NO_SEARCH_MATCH;

  const distance = boundedDamerauLevenshtein(queryToken, targetToken, maxDistance);
  if (distance > maxDistance) return NO_SEARCH_MATCH;

  return 1 + distance / Math.max(queryToken.length, targetToken.length);
}

export function getTextSearchScore(query: unknown, target: unknown, allowFuzzy = true): number {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return 0;

  const normalizedTarget = normalizeSearchText(target);
  if (!normalizedTarget) return NO_SEARCH_MATCH;
  if (normalizedTarget === normalizedQuery) return 0;
  if (normalizedTarget.startsWith(normalizedQuery)) return 0.05;
  if (normalizedTarget.includes(normalizedQuery)) return 0.1;
  if (!allowFuzzy) return NO_SEARCH_MATCH;

  const queryTokens = normalizedQuery.split(' ');
  const targetTokens = normalizedTarget.split(' ');
  let totalScore = 0;

  for (const queryToken of queryTokens) {
    let bestTokenScore = NO_SEARCH_MATCH;

    for (const targetToken of targetTokens) {
      bestTokenScore = Math.min(bestTokenScore, getTokenScore(queryToken, targetToken));
      if (bestTokenScore === 0) break;
    }

    if (!Number.isFinite(bestTokenScore)) return NO_SEARCH_MATCH;
    totalScore += bestTokenScore;
  }

  return totalScore / queryTokens.length;
}

export function getPatientSearchScore(
  query: unknown,
  patientName: unknown,
  identifiers: readonly unknown[] = [],
): number {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return 0;

  const compactQuery = compactSearchText(query);
  if (compactQuery) {
    const identifierMatch = identifiers.some((identifier) =>
      compactSearchText(identifier).includes(compactQuery),
    );
    if (identifierMatch) return 0.02;
  }

  return getTextSearchScore(normalizedQuery, patientName, true);
}

export function matchesPatientSearch(
  query: unknown,
  patientName: unknown,
  identifiers: readonly unknown[] = [],
): boolean {
  return Number.isFinite(getPatientSearchScore(query, patientName, identifiers));
}
